import React, { useState } from "react";
import styled from "styled-components";
import Text from "../atoms/Text";
import Button from "../atoms/Button";
import CheckBox from "../atoms/CheckBox";
import RadioButton from "../atoms/RadioButton";
import Input from "../atoms/Input";
import Switch from "../atoms/Switch";
import Dropdown, { DropdownMenuItem } from "../molecules/Dropdown";
import {
  PromptType,
  PROMPT_TYPE_KOREAN_NAMES,
  PERMISSION_PRESETS,
  PERMISSION_DESCRIPTIONS,
  RequestPresets,
  DOCS_TO_GUIDELINE_MAPPING,
  AVAILABLE_AI_MODELS,
} from "../../common/models/types";
import { isDoseExtractPromptType } from "../../common/models/doseExtractPrompt";
import { COLOR_SYSTEM } from "../design/design-system";
import { fetchPromptById } from "../../api/promptService";
import { ToastManager } from "../organisms/CommonUI";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 500px;
  max-width: 600px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  margin-bottom: 16px;
`;

const SectionDescription = styled.p`
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 14px;
  margin-top: 8px;
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionItem = styled.div<{ selected?: boolean; disabled?: boolean }>`
  padding: 12px 16px;
  border: 1px solid
    ${({ selected }) =>
      selected ? COLOR_SYSTEM.get("Blue")?.[400] : COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  background-color: ${({ selected, disabled }) =>
    disabled
      ? COLOR_SYSTEM.get("Gray")?.[50]
      : selected
      ? COLOR_SYSTEM.get("Blue")?.[50]
      : COLOR_SYSTEM.get("Gray")?.[0]};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ selected, disabled }) =>
      disabled
        ? COLOR_SYSTEM.get("Gray")?.[50]
        : selected
        ? COLOR_SYSTEM.get("Blue")?.[100]
        : COLOR_SYSTEM.get("Gray")?.[20]};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const PermissionSection = styled.div`
  margin-left: 20px;
  padding: 16px;
  border-left: 2px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  background-color: ${COLOR_SYSTEM.get("Gray")?.[25]};
  border-radius: 8px;
  margin-top: 12px;
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
`;

const PermissionItem = styled.div`
  display: flex;
  align-items: center;
`;

const PresetButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const PresetButton = styled.div`
  padding: 6px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 4px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }

  &:active {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const DetailSettingButton = styled.div`
  margin-left: auto;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const PromptIdInputContainer = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export interface AnalysisOptions {
  promptType: PromptType;
  promptId?: number;
  guidelineTypes: PromptType[];
  permissionsEnabled: boolean;
  selectedPermissions: string[];
  selectedModel?: string;
}

interface AnalysisOptionsTemplateProps {
  onSubmit: (options: AnalysisOptions) => void;
  onCancel: () => void;
}

const AnalysisOptionsTemplate: React.FC<AnalysisOptionsTemplateProps> = ({
  onSubmit,
  onCancel,
}) => {
  // PROMPT_TYPE_KOREAN_NAMES에서 프롬프트와 가이드라인 분리
  // dose_extract_* 는 룰 추출 지시문이라 환자 분석 선택지에서 제외한다(D-14 — 추출 지시문 + 환자
  // 데이터로 과금되는 AI 리뷰 호출 차단. 프론트의 유일한 enum 열거 게이트).
  const promptTypes = Object.entries(PROMPT_TYPE_KOREAN_NAMES)
    .filter(([key]) => !key.startsWith("guideline_") && !isDoseExtractPromptType(key))
    .map(([key, value]) => ({ key: key as PromptType, value }));

  const guidelineTypes = Object.entries(PROMPT_TYPE_KOREAN_NAMES)
    .filter(([key]) => key.startsWith("guideline_"))
    .map(([key, value]) => ({ key: key as PromptType, value }));

  const [selectedPromptType, setSelectedPromptType] = useState<PromptType>(
    PromptType.MEDICATION_COUNSELING
  );

  // 초기 가이드라인 설정을 RequestPresets에서 가져오기
  const initialPreset = RequestPresets[PromptType.MEDICATION_COUNSELING];
  const initialGuidelines = initialPreset?.docsOptions
    ?.map((doc) => DOCS_TO_GUIDELINE_MAPPING[doc])
    .filter((type): type is PromptType => type !== undefined) || [
    PromptType.GUIDELINE_BEERS2023,
    PromptType.GUIDELINE_SPSTv3,
  ];

  const [selectedGuidelineTypes, setSelectedGuidelineTypes] =
    useState<PromptType[]>(initialGuidelines);

  // promptId 직접 입력 관련 상태
  const [usePromptId, setUsePromptId] = useState<boolean>(false);
  const [promptId, setPromptId] = useState<string>("");
  const [isLoadingPrompt, setIsLoadingPrompt] = useState<boolean>(false);
  const [promptLoadError, setPromptLoadError] = useState<string>("");

  // 허가사항 관련 상태
  const [permissionsEnabled, setPermissionsEnabled] = useState<boolean>(true);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    initialPreset?.medicationOptions || PERMISSION_PRESETS.OPTION2 // RequestPresets에서 가져오거나 기본값
  );
  const [showPermissionDetails, setShowPermissionDetails] = useState<boolean>(false);

  // AI 모델 선택 관련 상태
  const [useModelSelection, setUseModelSelection] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string | undefined>(undefined);
  const [showModelDropdown, setShowModelDropdown] = useState<boolean>(false);

  const handlePromptTypeChange = (promptType: PromptType) => {
    setSelectedPromptType(promptType);
    setUsePromptId(false); // promptType 선택 시 promptId 사용 해제
    setPromptId(""); // promptId 값 초기화

    // RequestPresets에서 프롬프트 타입에 따른 기본 가이드라인 가져오기
    const preset = RequestPresets[promptType];
    let autoSelectedGuidelines: PromptType[] = [];

    if (preset && preset.docsOptions) {
      // docsOptions를 PromptType으로 변환
      autoSelectedGuidelines = preset.docsOptions
        .map((doc) => DOCS_TO_GUIDELINE_MAPPING[doc])
        .filter((type): type is PromptType => type !== undefined);
    }

    // 가이드라인이 없는 경우 기본값 설정
    if (autoSelectedGuidelines.length === 0) {
      autoSelectedGuidelines = [PromptType.GUIDELINE_BEERS2023, PromptType.GUIDELINE_SPSTv3];
    }

    setSelectedGuidelineTypes(autoSelectedGuidelines);

    // 허가사항도 RequestPresets에서 가져오기
    if (preset && preset.medicationOptions) {
      setSelectedPermissions(preset.medicationOptions);
    }
  };

  const handleGuidelineTypeToggle = (guidelineType: PromptType) => {
    setSelectedGuidelineTypes((prev) => {
      if (prev.includes(guidelineType)) {
        return prev.filter((type) => type !== guidelineType);
      } else {
        return [...prev, guidelineType];
      }
    });
  };

  // 허가사항 관련 핸들러
  const handlePermissionToggle = (permissionCode: string) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionCode)) {
        return prev.filter((code) => code !== permissionCode);
      } else {
        return [...prev, permissionCode];
      }
    });
  };

  const handlePermissionPreset = (presetType: keyof typeof PERMISSION_PRESETS) => {
    setSelectedPermissions(PERMISSION_PRESETS[presetType]);
  };

  // promptId 포커스 아웃 시 프롬프트 데이터 가져오기
  const handlePromptIdBlur = async () => {
    if (!promptId || !usePromptId) return;
    
    setIsLoadingPrompt(true);
    setPromptLoadError("");
    
    try {
      const prompt = await fetchPromptById(parseInt(promptId, 10));
      
      if (prompt.promptType) {
        // 프롬프트 타입 자동 설정
        setSelectedPromptType(prompt.promptType);
        
        // RequestPresets에서 프롬프트 타입에 따른 기본 가이드라인과 허가사항 가져오기
        const preset = RequestPresets[prompt.promptType];
        
        if (preset) {
          // 가이드라인 자동 설정
          if (preset.docsOptions) {
            const autoSelectedGuidelines = preset.docsOptions
              .map((doc) => DOCS_TO_GUIDELINE_MAPPING[doc])
              .filter((type): type is PromptType => type !== undefined);
            
            if (autoSelectedGuidelines.length > 0) {
              setSelectedGuidelineTypes(autoSelectedGuidelines);
            }
          }
          
          // 허가사항 자동 설정
          if (preset.medicationOptions) {
            setSelectedPermissions(preset.medicationOptions);
          }
        }
        
        ToastManager.show({
          content: `프롬프트 #${promptId}를 불러왔습니다: ${PROMPT_TYPE_KOREAN_NAMES[prompt.promptType] || prompt.promptType}`,
          duration: 2000,
        });
      }
    } catch (error) {
      console.error("프롬프트를 가져오는데 실패했습니다:", error);
      setPromptLoadError("프롬프트를 찾을 수 없습니다.");
      ToastManager.show({
        content: `프롬프트 #${promptId}를 찾을 수 없습니다.`,
        duration: 3000,
      });
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const handleSubmit = () => {
    const options: AnalysisOptions = {
      promptType: selectedPromptType,
      guidelineTypes: selectedGuidelineTypes,
      permissionsEnabled,
      selectedPermissions,
    };

    if (usePromptId && promptId) {
      options.promptId = parseInt(promptId, 10);
    }

    if (useModelSelection && selectedModel) {
      options.selectedModel = selectedModel;
    }

    onSubmit(options);
  };

  return (
    <FormContainer>
      {/* 프롬프트 선택 섹션 */}
      <Section>
        <SectionHeader>
          <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[700]}>
            분석 프롬프트 선택
          </Text>
          <SectionDescription>하나의 분석 프롬프트를 선택해주세요.</SectionDescription>
        </SectionHeader>
        <OptionGroup>
          {promptTypes.map(({ key, value }) => (
            <OptionItem
              key={key}
              selected={!usePromptId && selectedPromptType === key}
              onClick={() => handlePromptTypeChange(key)}
            >
              <RadioContainer>
                <RadioButton
                  value={key}
                  checked={!usePromptId && selectedPromptType === key}
                  onChange={() => handlePromptTypeChange(key)}
                  size="Medium"
                >
                  <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                    {value}
                  </Text>
                </RadioButton>
              </RadioContainer>
            </OptionItem>
          ))}
          {/* promptID로 실행하기 옵션 추가 */}
          <OptionItem
            selected={usePromptId}
            onClick={() => {
              setUsePromptId(true);
              setSelectedPromptType(PromptType.MEDICATION_COUNSELING); // 기본값으로 설정
            }}
          >
            <RadioContainer>
              <RadioButton
                value="usePromptId"
                checked={usePromptId}
                onChange={() => {
                  setUsePromptId(true);
                  setSelectedPromptType(PromptType.MEDICATION_COUNSELING); // 기본값으로 설정
                  setPromptId(""); // promptId 초기화
                  setPromptLoadError(""); // 에러 메시지 초기화
                }}
                size="Medium"
              >
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  promptID로 실행하기
                </Text>
              </RadioButton>
            </RadioContainer>
          </OptionItem>
        </OptionGroup>

        {/* promptId 입력 필드 */}
        {usePromptId && (
          <PromptIdInputContainer>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
              Prompt ID:
            </Text>
            <Input
              type="number"
              value={promptId}
              onChange={(e) => {
                setPromptId(e.target.value);
                setPromptLoadError(""); // 입력 시 에러 메시지 초기화
              }}
              onBlur={handlePromptIdBlur}
              placeholder="프롬프트 ID를 입력하세요"
              style={{ width: "200px" }}
              disabled={isLoadingPrompt}
            />
            {isLoadingPrompt && (
              <Text textType="Caption" color={COLOR_SYSTEM.get("Blue")?.[500]}>
                프롬프트 불러오는 중...
              </Text>
            )}
            {promptLoadError && (
              <Text textType="Caption" color={COLOR_SYSTEM.get("Red")?.[500]}>
                {promptLoadError}
              </Text>
            )}
          </PromptIdInputContainer>
        )}
      </Section>

      {/* 가이드라인 선택 섹션 */}
      <Section>
        <SectionHeader>
          <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[700]}>
            참조 가이드라인 선택
          </Text>
          <SectionDescription>
            분석에 참조할 가이드라인을 선택해주세요. (복수 선택 가능)
          </SectionDescription>
        </SectionHeader>
        <OptionGroup>
          {guidelineTypes.map(({ key, value }) => (
            <OptionItem
              key={key}
              selected={selectedGuidelineTypes.includes(key)}
              onClick={() => handleGuidelineTypeToggle(key)}
            >
              <CheckboxContainer onClick={(e) => e.stopPropagation()}>
                <CheckBox
                  checked={selectedGuidelineTypes.includes(key)}
                  onClick={() => handleGuidelineTypeToggle(key)}
                  size="Medium"
                >
                  <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                    {value}
                  </Text>
                </CheckBox>
              </CheckboxContainer>
            </OptionItem>
          ))}
        </OptionGroup>
      </Section>

      {/* 허가사항 선택 섹션 */}
      <Section>
        <SectionHeader>
          <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[700]}>
            허가사항 세부 선택
          </Text>
          <SectionDescription>분석에 포함할 허가사항을 선택해주세요.</SectionDescription>
        </SectionHeader>

        <OptionItem
          selected={permissionsEnabled}
          onClick={() => setPermissionsEnabled(!permissionsEnabled)}
        >
          <CheckboxContainer>
            <CheckBox
              checked={permissionsEnabled}
              onClick={() => setPermissionsEnabled(!permissionsEnabled)}
              size="Medium"
            >
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                허가사항
              </Text>
            </CheckBox>
          </CheckboxContainer>
          <DetailSettingButton
            onClick={(e) => {
              e.stopPropagation();
              setShowPermissionDetails(!showPermissionDetails);
            }}
          >
            <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[600]}>
              상세설정
            </Text>
          </DetailSettingButton>
        </OptionItem>

        {permissionsEnabled && showPermissionDetails && (
          <PermissionSection>
            <Text
              textType="Body2"
              color={COLOR_SYSTEM.get("Gray")?.[600]}
              style={{ marginBottom: "12px" }}
            >
              포함할 허가사항 항목을 선택해주세요.
            </Text>

            <PermissionGrid>
              {Object.entries(PERMISSION_DESCRIPTIONS).map(([code, description]) => (
                <PermissionItem key={code}>
                  <CheckBox
                    checked={selectedPermissions.includes(code)}
                    onClick={() => handlePermissionToggle(code)}
                    size="Small"
                  >
                    <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                      {code}
                    </Text>
                  </CheckBox>
                </PermissionItem>
              ))}
            </PermissionGrid>

            <PresetButtonGroup>
              <PresetButton onClick={() => handlePermissionPreset("ALL")}>
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  전체 선택
                </Text>
              </PresetButton>
              <PresetButton onClick={() => setSelectedPermissions([])}>
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  전체 해제
                </Text>
              </PresetButton>
              <PresetButton onClick={() => handlePermissionPreset("OPTION1")}>
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  옵션 1
                </Text>
              </PresetButton>
              <PresetButton onClick={() => handlePermissionPreset("OPTION2")}>
                <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  옵션 2
                </Text>
              </PresetButton>
            </PresetButtonGroup>

            <div style={{ marginTop: "12px" }}>
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                옵션 1: 전체 허가사항 (14개 항목) / 옵션 2: 핵심 허가사항 (8개 항목)
              </Text>
            </div>
          </PermissionSection>
        )}
      </Section>

      {/* AI 모델 선택 섹션 */}
      <Section>
        <SectionHeader>
          <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[700]}>
            AI 모델 선택
          </Text>
          <SectionDescription>분석에 사용할 AI 모델을 지정할 수 있습니다.</SectionDescription>
        </SectionHeader>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px" }}>
          <Switch
            size="Medium"
            isActive={useModelSelection}
            onClick={() => {
              setUseModelSelection(!useModelSelection);
              if (!useModelSelection && !selectedModel) {
                setSelectedModel("GEMINI_2_5_PRO"); // 기본값 설정
              } else if (useModelSelection) {
                setSelectedModel(undefined);
              }
            }}
          />
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
            AI 모델 지정
          </Text>
        </div>

        {useModelSelection && (
          <div style={{ marginTop: "16px", paddingLeft: "20px" }}>
            <Dropdown
              defaultValue="모델 선택"
              selected={selectedModel || "모델 선택"}
              size="Medium"
              buttonStyle="Outlined"
              onClick={() => setShowModelDropdown(!showModelDropdown)}
              active={showModelDropdown}
              buttonCustomStyle={{
                color: COLOR_SYSTEM.get("Gray")?.[700],
                borderColor: COLOR_SYSTEM.get("Gray")?.[300],
                backgroundColor: COLOR_SYSTEM.get("Gray")?.[0],
                minWidth: "200px",
              }}
              customStyle={{
                minWidth: "200px",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {AVAILABLE_AI_MODELS.map((model) => (
                <DropdownMenuItem
                  key={model}
                  onClick={() => {
                    setSelectedModel(model);
                    setShowModelDropdown(false);
                  }}
                >
                  <Text
                    textType="Body2"
                    color={
                      selectedModel === model
                        ? COLOR_SYSTEM.get("Blue")?.[600]
                        : COLOR_SYSTEM.get("Gray")?.[700]
                    }
                  >
                    {model.replace(/_/g, " ")}
                  </Text>
                </DropdownMenuItem>
              ))}
            </Dropdown>
          </div>
        )}
      </Section>

      <ButtonContainer>
        <Button
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={onCancel}
        >
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
            취소
          </Text>
        </Button>
        <Button
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={handleSubmit}
        >
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[0]}>
            분석 시작
          </Text>
        </Button>
      </ButtonContainer>
    </FormContainer>
  );
};

export default AnalysisOptionsTemplate;
